import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { friendsAPI, gamesAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import ProfileAvatar from './ProfileAvatar';

interface Friend {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture?: string;
  status: 'online' | 'offline';
}

interface FriendRequest {
  id: string;
  user: Friend;
  createdAt: string;
}

const PLAY_VARIANTS = [
  { key: 'lower_green', label: 'Pharaon', icon: '🃏', defaultPlayers: 4, gamePoints: 100 },
  { key: 'raub', label: 'Raub', icon: '⚔️', defaultPlayers: 4, gamePoints: 100 },
  { key: 'sedmica', label: 'Sedmica', icon: '7️⃣', defaultPlayers: 4, gamePoints: 120 },
];

const Friends: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [activeTab, setActiveTab] = useState<'friends' | 'received' | 'sent'>('friends');
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [friendEmail, setFriendEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [inviteMenuFor, setInviteMenuFor] = useState<string | null>(null);
  const [invitingFriend, setInvitingFriend] = useState<string | null>(null);

  const onlineFriends = friends.filter(f => f.status === 'online');

  const handleInviteToPlay = async (friend: Friend, variant: typeof PLAY_VARIANTS[number]) => {
    if (!user) return;
    setInvitingFriend(friend.id);
    try {
      const created = await gamesAPI.create({
        name: `${user.firstName} & ${friend.firstName} - ${variant.label}`,
        maxPlayers: variant.defaultPlayers,
        decks: 1,
        dealtCards: 4,
        cardsPerDeal: 2,
        gamePoints: variant.gamePoints,
        type: 'public',
        gameVariant: variant.key,
        gameMode: 'friendly',
        entryFee: 0,
        platformFee: 5,
      });
      const gameId = created.data?.id;
      if (gameId) {
        await gamesAPI.inviteFriend(gameId, friend.id);
        setInviteMenuFor(null);
        navigate(`/games/${gameId}`);
      }
    } catch (error: any) {
      setMessage({
        text: error.response?.data?.message || t('friends.failedInvite'),
        type: 'error',
      });
    } finally {
      setInvitingFriend(null);
    }
  };

  useEffect(() => {
    loadFriends();
    loadPendingRequests();
    
    // Initialize socket
            const newSocket = io('/', {
      auth: { token: localStorage.getItem('token') }
    });
    setSocket(newSocket);

    newSocket.on('friendRequestReceived', loadPendingRequests);
    newSocket.on('friendRequestAccepted', () => {
      loadFriends();
      loadPendingRequests();
    });
    newSocket.on('userStatusChanged', loadFriends);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadFriends = async () => {
    try {
      const response = await friendsAPI.getFriends();
      setFriends(response.data);
    } catch (error) {
      console.error('Error loading friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const [receivedResponse, sentResponse] = await Promise.all([
        friendsAPI.getReceivedRequests(),
        friendsAPI.getSentRequests()
      ]);
      setPendingRequests(receivedResponse.data);
      setSentRequests(sentResponse.data);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const handleAddFriend = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await friendsAPI.sendRequest(friendEmail);
      setMessage({ text: t('friends.requestSentSuccess'), type: 'success' });
      setFriendEmail('');
      setShowAddFriendModal(false);
      loadPendingRequests();
    } catch (error: any) {
      setMessage({ 
        text: error.response?.data?.message || t('friends.failedSendRequest'), 
        type: 'error' 
      });
    }
  };

  const handleAcceptRequest = async (userId: string) => {
    try {
      await friendsAPI.acceptRequest(userId);
      setMessage({ text: t('friends.requestAcceptedMsg'), type: 'success' });
      loadFriends();
      loadPendingRequests();
    } catch (error: any) {
      setMessage({ 
        text: error.response?.data?.message || t('friends.failedAccept'), 
        type: 'error' 
      });
    }
  };

  const handleRejectRequest = async (userId: string) => {
    try {
      await friendsAPI.rejectRequest(userId);
      setMessage({ text: t('friends.requestRejectedMsg'), type: 'success' });
      loadPendingRequests();
    } catch (error: any) {
      setMessage({ 
        text: error.response?.data?.message || t('friends.failedReject'), 
        type: 'error' 
      });
    }
  };

  return (
    <div className="dashboard-modern">
      {/* Friends Header */}
      <div className="dashboard-header">
        <div>
          <h1>👥 {t('friends.title')}</h1>
          <p>{t('friends.subtitle')}</p>
        </div>
        <button 
          className="action-btn"
          onClick={() => setShowAddFriendModal(true)}
        >
          <span>➕</span>
          {t('friends.addFriend')}
        </button>
      </div>

      {/* Status Message */}
      {message && (
        <div style={{
          padding: '16px 20px',
          marginBottom: '24px',
          borderRadius: '8px',
          backgroundColor: message.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
          border: `1px solid ${message.type === 'success' ? '#4CAF50' : '#f44336'}`,
          color: message.type === 'success' ? '#4CAF50' : '#f44336',
        }}>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[
          { key: 'friends', label: t('friends.myFriends'), count: friends.length },
          { key: 'received', label: t('friends.received'), count: pendingRequests.length },
          { key: 'sent', label: t('friends.sent'), count: sentRequests.length }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              background: activeTab === tab.key ? '#769656' : '#3d3a33',
              color: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Content based on active tab */}
      {activeTab === 'friends' && (
        <div className="game-section">
          <h3>
            <span>👥</span>
            {t('friends.myFriends')} ({friends.length})
          </h3>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#a0a0a0' }}>
              {t('friends.loadingFriends')}
            </div>
          ) : friends.length === 0 ? (
            <div className="game-card modern">
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.3 }}>👥</div>
                <h4 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '20px' }}>{t('friends.noFriendsTitle')}</h4>
                <p style={{ color: '#a0a0a0', marginBottom: '24px', lineHeight: '1.6' }}>
                  {t('friends.noFriendsDesc')}
                </p>
                <button 
                  className="action-btn"
                  onClick={() => setShowAddFriendModal(true)}
                >
                  <span>➕</span>
                  {t('friends.addFirstFriend')}
                </button>
              </div>
            </div>
          ) : (
            <div className="games-grid">
              {friends.map((friend) => (
                <div key={friend.id} className="friend-card">
                  <div className="friend-card-top">
                    <div className="friend-card-avatar">
                      <ProfileAvatar user={friend} size={48} />
                      <span className={`friend-status-dot ${friend.status}`} title={friend.status} />
                    </div>
                    <div className="friend-card-id">
                      <div className="friend-card-name">{friend.firstName} {friend.lastName}</div>
                      <div className="friend-card-email">{friend.email}</div>
                    </div>
                    <span className={`friend-status-pill ${friend.status}`}>
                      {friend.status === 'online' ? t('friends.online') : t('friends.offline')}
                    </span>
                  </div>

                  <div className="friend-card-actions">
                    <button
                      className="friend-invite-btn"
                      onClick={() => setInviteMenuFor(inviteMenuFor === friend.id ? null : friend.id)}
                      disabled={invitingFriend === friend.id}
                    >
                      {invitingFriend === friend.id ? t('friends.sending') : `🎮 ${t('friends.inviteToPlay')}`}
                    </button>
                  </div>

                  {inviteMenuFor === friend.id && (
                    <div className="friend-invite-menu">
                      {PLAY_VARIANTS.map((v) => (
                        <button
                          key={v.key}
                          className="friend-invite-option"
                          onClick={() => handleInviteToPlay(friend, v)}
                          disabled={invitingFriend === friend.id}
                        >
                          <span>{v.icon}</span> {v.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'received' && (
        <div className="game-section">
          <h3>
            <span>📨</span>
            {t('friends.receivedRequests')} ({pendingRequests.length})
          </h3>
          
          {pendingRequests.length === 0 ? (
            <div className="game-card modern">
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.3 }}>📨</div>
                <h4 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '20px' }}>{t('friends.noPendingRequests')}</h4>
                <p style={{ color: '#a0a0a0', marginBottom: '24px', lineHeight: '1.6' }}>
                  {t('friends.noPendingDesc')}
                </p>
              </div>
            </div>
          ) : (
            <div className="games-grid">
              {pendingRequests.map((request) => (
                <div key={request.id} className="game-card modern">
                  <div className="game-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <ProfileAvatar user={request.user} size={48} />
                      <div style={{ flex: 1 }}>
                        <h4 className="game-name">{request.user.firstName} {request.user.lastName}</h4>
                        <p className="game-description">{request.user.email}</p>
                      </div>
                    </div>
                    <span className="game-status waiting">{t('friends.new')}</span>
                  </div>
                  <div className="game-info">
                    <div className="info-row">
                      <span>{t('friends.received')}</span>
                      <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('friends.time')}</span>
                      <span>{new Date(request.createdAt).toLocaleTimeString()}</span>
                    </div>
                  </div>
                  <div style={{ padding: '16px', display: 'flex', gap: '12px' }}>
                    <button 
                      className="action-btn"
                      onClick={() => handleAcceptRequest(request.user.id)}
                      style={{ flex: 1, background: '#4CAF50' }}
                    >
                      <span>✓</span>
                      {t('friends.accept')}
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleRejectRequest(request.user.id)}
                      style={{ flex: 1, background: '#f44336' }}
                    >
                      <span>✕</span>
                      {t('friends.reject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'sent' && (
        <div className="game-section">
          <h3>
            <span>📤</span>
            {t('friends.sentRequests')} ({sentRequests.length})
          </h3>
          
          {sentRequests.length === 0 ? (
            <div className="game-card modern">
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.3 }}>📤</div>
                <h4 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '20px' }}>{t('friends.noSentTitle')}</h4>
                <p style={{ color: '#a0a0a0', marginBottom: '24px', lineHeight: '1.6' }}>
                  {t('friends.noSentDesc')}
                </p>
              </div>
            </div>
          ) : (
            <div className="games-grid">
              {sentRequests.map((request) => (
                <div key={request.id} className="game-card modern">
                  <div className="game-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <ProfileAvatar user={request.user} size={48} />
                      <div style={{ flex: 1 }}>
                        <h4 className="game-name">{request.user.firstName} {request.user.lastName}</h4>
                        <p className="game-description">{request.user.email}</p>
                      </div>
                    </div>
                    <span className="game-status waiting">{t('friends.pending')}</span>
                  </div>
                  <div className="game-info">
                    <div className="info-row">
                      <span>{t('friends.sent')}</span>
                      <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('friends.status')}</span>
                      <span style={{ color: '#FF9800' }}>{t('friends.waitingForResponse')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Friends Stats (moved to bottom) */}
      <div className="stats-grid" style={{ marginTop: '40px' }}>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#4CAF50' }}>{friends.length}</div>
          <div className="stat-label">{t('friends.totalFriends')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#4CAF50' }}>{onlineFriends.length}</div>
          <div className="stat-label">{t('friends.onlineNow')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#FF9800' }}>{pendingRequests.length}</div>
          <div className="stat-label">{t('friends.pendingRequestsStat')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: '#2196F3' }}>{sentRequests.length}</div>
          <div className="stat-label">{t('friends.sentRequestsStat')}</div>
        </div>
      </div>

      {/* Add Friend Modal */}
      {showAddFriendModal && (
        <div 
          className="modal-overlay" 
          onClick={() => setShowAddFriendModal(false)}
        >
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title">👥 {t('friends.addFriend')}</h3>
              <button 
                className="modal-close-btn"
                onClick={() => setShowAddFriendModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                {t('friends.addFriendDesc')}
              </p>
              
              <form onSubmit={handleAddFriend}>
                <div className="form-group">
                  <label htmlFor="friendEmail" className="form-label">
                    {t('friends.friendEmailLabel')}
                  </label>
                  <input
                    id="friendEmail"
                    type="email"
                    value={friendEmail}
                    onChange={(e) => setFriendEmail(e.target.value)}
                    required
                    placeholder={t('friends.emailPlaceholder')}
                    className="form-input"
                  />
                  <div className="form-hint">
                    💡 {t('friends.emailTip')}
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="action-btn secondary"
                    onClick={() => setShowAddFriendModal(false)}
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    type="submit" 
                    className="action-btn"
                  >
                    ✉️ {t('friends.sendRequest')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Friends; 